import { FC, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { diffLines } from "diff";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { AppLoader } from "../../shared/components/AppLoader";
import { getRevisionDiff, type RevisionDiffEntry } from "../../shared/services/promptTests";

type TLineCounts = { added: number; removed: number };

function countLines(entry: RevisionDiffEntry): TLineCounts {
  return diffLines(entry.leftContent, entry.rightContent).reduce<TLineCounts>(
    (totals, part) => {
      if (!part.added && !part.removed) return totals;
      const lines = part.count ?? part.value.split("\n").length;
      return part.added ? { ...totals, added: totals.added + lines } : { ...totals, removed: totals.removed + lines };
    },
    { added: 0, removed: 0 }
  );
}

const EntryDiff: FC<{ entry: RevisionDiffEntry }> = ({ entry }) => {
  const parts = useMemo(() => diffLines(entry.leftContent, entry.rightContent), [entry]);

  return (
    <pre className="max-h-[320px] overflow-auto rounded border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-[11px] leading-[16px]">
      {parts.map((part, index) => (
        <span
          key={index}
          className={
            part.added
              ? "block bg-green-100 text-green-900"
              : part.removed
                ? "block bg-red-100 text-red-900"
                : "block text-gray-500"
          }
        >
          {part.value.replace(/\n$/, "")}
        </span>
      ))}
    </pre>
  );
};

type TProps = {
  candidateRevisionId: number;
  baselineRevisionId: number;
};

export const PromptChangeSummary: FC<TProps> = ({ candidateRevisionId, baselineRevisionId }) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["prompt-revision-diff", candidateRevisionId, baselineRevisionId],
    queryFn: () => getRevisionDiff(candidateRevisionId, baselineRevisionId)
  });

  const changed = (data?.entries ?? []).filter((entry) => entry.changed);

  return (
    <div className="mb-6 rounded-lg border bg-card px-6 py-4">
      <FigmaTypography component="div" variant="body-2" className="text-gray-900">
        What changed in the prompts
      </FigmaTypography>

      {isLoading ? (
        <AppLoader />
      ) : isError || !data ? (
        <FigmaTypography component="div" variant="body-3" className="text-red-600 mt-2">
          Failed to load the prompt diff.
        </FigmaTypography>
      ) : changed.length === 0 ? (
        <FigmaTypography component="div" variant="body-3" className="text-gray-500 mt-2">
          The candidate uses the same prompt text as the baseline — any difference in the runs is model variance.
        </FigmaTypography>
      ) : (
        <div className="mt-2 divide-y border-t">
          {changed.map((entry) => {
            const counts = countLines(entry);
            const expanded = expandedKey === entry.promptKey;

            return (
              <div key={entry.promptKey} className="py-2">
                <button
                  type="button"
                  onClick={() => setExpandedKey(expanded ? null : entry.promptKey)}
                  className="flex w-full items-center gap-2 text-left"
                >
                  {expanded ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />}
                  <span className="text-[13px] text-gray-800">{entry.label}</span>
                  <span className="text-[11px] text-gray-400">{entry.promptKey}</span>
                  <span className="ml-auto shrink-0 text-[12px]">
                    <span className="text-green-700">+{counts.added}</span>{" "}
                    <span className="text-red-700">-{counts.removed}</span>
                  </span>
                </button>
                {expanded && (
                  <div className="mt-2">
                    <EntryDiff entry={entry} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
